import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  TextField,
  Input,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  CodeOutlined as CodeIcon,
  AccessTimeOutlined as TimeIcon,
  CloudUploadOutlined as UploadIcon,
  CheckCircleOutlined as CheckIcon,
  InfoOutlined as InfoIcon,
  InsertDriveFileOutlined as FileIcon,
  ArrowBackOutlined as BackIcon,
  TaskOutlined as TaskIcon,
} from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import CandidateHeader from '../../components/CandidateHeader';
import api from '../../api';
import { showSuccess, showError, showDeleteConfirmation } from '../../utils/sweetAlertConfig';

const CandidateTechnicalTask = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [task, setTask] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [taskStarted, setTaskStarted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const [formData, setFormData] = useState({
    submission_text: '',
    submission_notes: '',
    file: null
  });

  useEffect(() => {
    loadCandidateData();
    loadTaskDetails();
  }, [taskId]);

  // Timer Effect
  useEffect(() => {
    let interval = null;
    
    if (taskStarted && timeRemaining > 0 && !submission) {
      interval = setInterval(() => {
        // Double-check against localStorage to prevent desync
        const taskStartTime = localStorage.getItem(`task_${taskId}_start_time`);
        if (taskStartTime && task) {
          const startTime = parseInt(taskStartTime);
          const now = Date.now();
          const elapsedSeconds = Math.floor((now - startTime) / 1000);
          const totalSeconds = task.deadline_hours * 60 * 60;
          const actualRemainingSeconds = totalSeconds - elapsedSeconds;
          
          if (actualRemainingSeconds <= 0) {
            setTimerExpired(true);
            setTimeRemaining(0);
            showError('Time Expired', 'The time limit for this task has expired. Please submit whatever you have completed.');
            return;
          }
          
          setTimeRemaining(actualRemainingSeconds);
        } else {
          // Fallback to simple countdown
          setTimeRemaining(prevTime => {
            if (prevTime <= 1) {
              setTimerExpired(true);
              showError('Time Expired', 'The time limit for this task has expired. Please submit whatever you have completed.');
              return 0;
            }
            return prevTime - 1;
          });
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [taskStarted, timeRemaining, submission, taskId, task]);

  const loadCandidateData = () => {
    const stored = localStorage.getItem('candidate_data');
    if (stored) {
      setCandidate(JSON.parse(stored));
    }
  };

  const loadTaskDetails = async () => {
    try {
      const response = await api.get(`/candidate/technical-task/${taskId}`);
      setTask(response.data.task);
      setSubmission(response.data.submission);
      
      // Check if task was already started
      const taskStatus = localStorage.getItem(`task_${taskId}_started`);
      const taskStartTime = localStorage.getItem(`task_${taskId}_start_time`);
      
      if (taskStatus === 'true' && taskStartTime && !response.data.submission) {
        const startTime = parseInt(taskStartTime);
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        const totalSeconds = response.data.task.deadline_hours * 60 * 60; // Convert hours to seconds
        const remainingSeconds = totalSeconds - elapsedSeconds;
        
        console.log('Restoring timer state:');
        console.log('Start time:', new Date(startTime).toLocaleString());
        console.log('Current time:', new Date(now).toLocaleString());
        console.log('Elapsed seconds:', elapsedSeconds);
        console.log('Total seconds:', totalSeconds);
        console.log('Remaining seconds:', remainingSeconds);
        
        if (remainingSeconds > 0) {
          setTaskStarted(true);
          setTimeRemaining(remainingSeconds);
        } else {
          setTaskStarted(true);
          setTimerExpired(true);
          setTimeRemaining(0);
        }
      }
    } catch (error) {
      console.error('Error loading task details:', error);
      showError('Error', 'Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTask = async () => {
    try {
      const result = await showDeleteConfirmation(
        'Start Technical Task',
        `Are you sure you want to start this task? The timer will begin immediately and you will have ${task.deadline_hours} hour(s) to complete it.`
      );

      if (!result.isConfirmed) return;

      // Start the task
      await api.post(`/candidate/assessment/${taskId}/start`);
      
      const startTime = Date.now();
      const totalSeconds = task.deadline_hours * 60 * 60; // Convert hours to seconds
      
      console.log('Starting task:');
      console.log('Start time:', new Date(startTime).toLocaleString());
      console.log('Total seconds:', totalSeconds);
      console.log('Deadline hours:', task.deadline_hours);
      
      // Save start time to localStorage
      localStorage.setItem(`task_${taskId}_started`, 'true');
      localStorage.setItem(`task_${taskId}_start_time`, startTime.toString());
      
      setTaskStarted(true);
      setTimeRemaining(totalSeconds);
      
      showSuccess('Task Started', 'Your timer has started. Good luck!');
      
    } catch (error) {
      console.error('Error starting task:', error);
      showError('Error', 'Failed to start task');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size
      const maxSize = task?.max_file_size_mb * 1024 * 1024; // Convert to bytes
      if (file.size > maxSize) {
        showError('File Too Large', `File size must be less than ${task.max_file_size_mb}MB`);
        return;
      }

      // Check file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const allowedTypes = task?.allowed_file_types?.split(',').map(type => type.trim());
      if (allowedTypes && !allowedTypes.includes(fileExtension)) {
        showError('Invalid File Type', `Allowed file types: ${task.allowed_file_types}`);
        return;
      }

      setFormData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!taskStarted) {
      showError('Task Not Started', 'Please start the task first');
      return;
    }

    if (timerExpired && timeRemaining <= 0) {
      showError('Time Expired', 'The time limit has expired. You can no longer submit this task.');
      return;
    }
    
    if (!formData.file && task?.submission_type === 'file') {
      showError('File Required', 'Please select a file to upload');
      return;
    }

    if (!formData.submission_text && task?.submission_type === 'text') {
      showError('Text Required', 'Please enter your submission text');
      return;
    }

    const result = await showDeleteConfirmation(
      'Submit Task',
      'Are you sure you want to submit this task? You cannot modify it after submission.'
    );

    if (!result.isConfirmed) return;

    try {
      setSubmitting(true);
      
      const submitData = new FormData();
      submitData.append('technical_assessment_id', taskId);
      submitData.append('submission_text', formData.submission_text);
      submitData.append('submission_notes', formData.submission_notes);
      
      if (formData.file) {
        submitData.append('file', formData.file);
      }

      await api.post('/candidate/technical-task/submit', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Clear localStorage
      localStorage.removeItem(`task_${taskId}_started`);
      localStorage.removeItem(`task_${taskId}_start_time`);

      showSuccess('Success', 'Task submitted successfully!');
      loadTaskDetails(); // Reload to show submitted status
      
    } catch (error) {
      console.error('Error submitting task:', error);
      showError('Error', 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '00:00:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (!timeRemaining) return '#68B977';
    
    const totalTime = task?.deadline_hours * 3600;
    const percentage = (timeRemaining / totalTime) * 100;
    
    if (percentage > 50) return '#68B977'; // Green
    if (percentage > 25) return '#ffc107'; // Yellow
    return '#dc3545'; // Red
  };

  const formatDeadline = (hours) => {
    if (hours <= 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days} day${days !== 1 ? 's' : ''}${remainingHours > 0 ? ` ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}` : ''}`;
    }
  };

  const getSubmissionStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'primary';
      case 'under_review': return 'warning';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)' }}>
          <CandidateHeader candidate={candidate} />
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Loading task details...</Typography>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  if (!task) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)' }}>
          <CandidateHeader candidate={candidate} />
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <TaskIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Task Not Found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The requested technical task could not be found.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/candidate/applications')}>
                Back to Applications
              </Button>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)' }}>
        <CandidateHeader candidate={candidate} />

        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate('/candidate/applications')}
              sx={{ mb: 2, color: '#29204C' }}
            >
              Back to Applications
            </Button>
            
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                color: '#29204C',
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Technical Task
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Complete the task below and submit your solution
            </Typography>
          </Box>

          {/* Task Details Card */}
          <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Task Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CodeIcon sx={{ mr: 2, color: '#68B977', fontSize: '2rem' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#29204C' }}>
                    {task.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Task ID: {task.technical_assessment_id}
                  </Typography>
                </Box>
                {submission && (
                  <Chip
                    label={(submission.status || 'pending').replace('_', ' ').toUpperCase()}
                    color={getSubmissionStatusColor(submission.status)}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>

              {/* Timer Section - Show when task is started */}
              {taskStarted && !submission && (
                <Box sx={{ 
                  textAlign: 'center', 
                  mb: 3, 
                  p: 3, 
                  backgroundColor: getTimeColor() === '#dc3545' ? 'rgba(220, 53, 69, 0.1)' : 
                                   getTimeColor() === '#ffc107' ? 'rgba(255, 193, 7, 0.1)' : 
                                   'rgba(104, 185, 119, 0.1)',
                  borderRadius: 3,
                  border: `2px solid ${getTimeColor()}`
                }}>
                  <Typography variant="h6" sx={{ color: getTimeColor(), fontWeight: 600, mb: 1 }}>
                    {timerExpired ? 'TIME EXPIRED' : 'TIME REMAINING'}
                  </Typography>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      color: getTimeColor(), 
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      mb: 1
                    }}
                  >
                    {formatTime(timeRemaining)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {timerExpired ? 'Please submit your work immediately' : 'Task in progress - you can safely navigate away and return'}
                  </Typography>
                </Box>
              )}

              {/* Start Task Button */}
              {!taskStarted && !submission && (
                <>
                  <Divider sx={{ my: 3 }} />
                  
                  <Box sx={{ textAlign: 'center', mb: 3, p: 3, backgroundColor: '#f8f9fa', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#29204C' }}>
                       Ready to Begin Your Technical Challenge?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Once you start, you'll see the full task description and instructions.<br/>
                      The timer will begin immediately, so make sure you're ready!
                    </Typography>
                    
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleStartTask}
                      startIcon={<TimeIcon />}
                      sx={{
                        backgroundColor: '#68B977',
                        px: 4,
                        py: 2,
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        boxShadow: 3,
                        '&:hover': {
                          backgroundColor: '#4a8455',
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      Start Technical Task
                    </Button>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                       Time Limit: <strong>{formatDeadline(task.deadline_hours)}</strong>
                    </Typography>
                  </Box>
                </>
              )}

              {/* Task Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <TimeIcon sx={{ color: '#68B977', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Time Limit</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDeadline(task.deadline_hours)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <FileIcon sx={{ color: '#68B977', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Max File Size</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {task.max_file_size_mb}MB
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <InfoIcon sx={{ color: '#68B977', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Submission Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {task.submission_type.charAt(0).toUpperCase() + task.submission_type.slice(1)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Show task details only after starting */}
              {taskStarted && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Description */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {task.description}
                  </Typography>

                  {/* Instructions */}
                  {task.instructions && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Instructions
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {task.instructions}
                      </Typography>
                    </>
                  )}

                  {/* File Types */}
                  {task.allowed_file_types && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Allowed File Types
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        {task.allowed_file_types.split(',').map((type, index) => (
                          <Chip 
                            key={index}
                            label={`.${type.trim()}`}
                            size="small"
                            sx={{ mr: 1, mb: 1, backgroundColor: '#f5f5f5' }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Submission Section */}
          {submission ? (
            // Show existing submission
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#29204C' }}>
                  Your Submission
                </Typography>
                
                <Alert severity="success" sx={{ mb: 3 }}>
                  Task submitted successfully on {new Date(submission.submitted_at).toLocaleString()}
                </Alert>

                {submission.submission_text && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Submission Text:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                      {submission.submission_text}
                    </Typography>
                  </Box>
                )}

                {submission.file_name && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Uploaded File:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                      <FileIcon sx={{ mr: 1, color: '#68B977' }} />
                      <Typography variant="body2">{submission.file_name}</Typography>
                    </Box>
                  </Box>
                )}

                {submission.submission_notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Notes:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                      {submission.submission_notes}
                    </Typography>
                  </Box>
                )}

                {submission.score && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Score:</strong> {submission.score} / {task.max_score} points
                    </Typography>
                    {submission.feedback && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Feedback:</strong> {submission.feedback}
                      </Typography>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          ) : taskStarted ? (
            // Show submission form only after task is started
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#29204C' }}>
                  Submit Your Solution
                </Typography>

                {timerExpired && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    ⏰ Time has expired! Please submit your work immediately.
                  </Alert>
                )}

                {timeRemaining <= 300 && timeRemaining > 0 && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    ⚠️ Less than 5 minutes remaining! Prepare to submit your work.
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Text Submission */}
                  {(task.submission_type === 'text' || task.submission_type === 'code') && (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      name="submission_text"
                      label={task.submission_type === 'code' ? 'Your Code' : 'Your Text Submission'}
                      value={formData.submission_text}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      sx={{ mb: 3 }}
                      placeholder={task.submission_type === 'code' ? 'Paste your code here...' : 'Enter your submission text...'}
                    />
                  )}

                  {/* File Upload */}
                  {(task.submission_type === 'file' || task.submission_type === 'image') && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Upload File *
                      </Typography>
                      <Input
                        type="file"
                        onChange={handleFileChange}
                        inputProps={{ 
                          accept: task.allowed_file_types.split(',').map(type => `.${type.trim()}`).join(',')
                        }}
                        sx={{ 
                          width: '100%',
                          p: 2,
                          border: '2px dashed #e0e0e0',
                          borderRadius: 2,
                          '&:hover': {
                            borderColor: '#29204C'
                          }
                        }}
                      />
                      {formData.file && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                          <FileIcon sx={{ mr: 1, color: '#68B977' }} />
                          <Typography variant="body2">{formData.file.name}</Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Additional Notes */}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="submission_notes"
                    label="Additional Notes (Optional)"
                    value={formData.submission_notes}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ mb: 3 }}
                    placeholder="Any additional comments or notes about your submission..."
                  />

                  {/* Submit Button */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      startIcon={submitting ? null : <UploadIcon />}
                      sx={{
                        backgroundColor: '#29204C',
                        px: 4,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#1a1530'
                        }
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit Task'}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          ) : (
            // Show message when task not started
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <TaskIcon sx={{ fontSize: 60, color: '#29204C', opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#29204C' }}>
                  Ready to Start?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Click the "Start Technical Task" button above to begin your assessment.<br/>
                  Make sure you have everything ready before starting as the timer will begin immediately.
                </Typography>
                <Alert severity="info">
                  <Typography variant="body2">
                     <strong>Tip:</strong> You can safely navigate away from this page after starting the task. 
                    Your timer will continue running and you can return anytime to submit your work.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CandidateTechnicalTask;